/**
 *  @fileOverview tab module
 *
 *  @author Andras Muk <andras.muk@possible.com>
 */

import forEach from 'lodash/forEach';
import detectIt from 'detect-it';
import 'whatwg-fetch';
import { customSelectController } from './custom-select-controller';
import { accordion } from './accordion';
import { video } from './video';
import { imageLoader } from './image-loader';

const URL = window.location.hostname.match(/localhost|10\.|127\./ig) ? 'http://futurefocus.possible.hu' : '';

class Tab {
  init() {
    this.isLoading = false;
    this.activeTab = null;
    this.initDOMElements();
    if (this.tabSection !== null) {
      this.initBindings();
    }
  }

  initDOMElements() {
    this.tabSection = document.body.querySelector('[data-tab]');
    if (this.tabSection !== null) {
      this.tabButtons = this.tabSection.querySelectorAll('[data-tab-button]');
      this.tabContent = this.tabSection.querySelector('[data-tab-content]');
    }
  }

  initBindings() {
    this.select = customSelectController.getSelectByName('information-centre');
    this.select.setChangeCallback((select, url) => {
      this.setActiveTab(url);
    });

    this.bindPagers();

    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.url) {
        this.setActiveTab(e.state.url, true);
      }
    }, false);

    forEach(this.tabButtons, (link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActiveTab(link.getAttribute('href'));
      }, false);

      if (detectIt.deviceType !== 'mouseOnly') {
        link.addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.setActiveTab(link.getAttribute('href'));
        }, false);
      }
    });
  }

  bindPagers() {
    forEach(this.tabSection.querySelectorAll('[data-tab-pager] [data-tab-page]'), (pager) => {
      pager.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActiveTab(pager.getAttribute('href'));
      }, false);
    });
  }

  setActiveTab(url, fromHistory = false) {
    if (this.tabButtons !== null) {
      forEach(this.tabButtons, (button) => {
        button.classList.remove('active');
        button.blur();
        button.setAttribute('aria-selected', 'false');
      });
    }

    if (this.isLoading === false && this.activeTab !== url) {
      Promise.all([this.fetchTabContent(url)]).then(() => {
        this.isLoading = false;
        this.activeTab = url;

        if (fromHistory === false) {
          window.history.pushState({
            url,
          }, document.title, url);
        }

        this.updateTabButton(url);
        this.callTealiumView(url);
      }).catch((reason) => {
        this.isLoading = false;
        console.warn(reason);
      });
    }
  }

  fetchTabContent(tabUrl) {
    this.isLoading = true;

    if (tabUrl.match(/page=/ig) === null) {
      /* eslint-disable no-param-reassign */
      tabUrl += '?page=1';
      /* eslint-enable no-param-reassign */
    }

    return fetch(`${URL}${tabUrl}&a=1`, { credentials: 'same-origin' })
      .then(this.checkStatus)
      .then(response => (response.text()))
      .then((responseHtml) => {
        this.parseAndReplaceContent(responseHtml);
      });
  }

  checkStatus(response) {
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }

  parseAndReplaceContent(html) {
    const tempElement = document.createElement('div');
    tempElement.style.display = 'none';
    tempElement.innerHTML = html;

    const content = tempElement.querySelector('[data-tab-content]').innerHTML;

    if (content !== null && content !== undefined) {
      this.tabContent.innerHTML = content;
      this.tabContent = this.tabSection.querySelector('[data-tab-content]');

      this.bindPagers();

      if (this.tabContent.querySelectorAll('[data-img-src]').length > 0) {
        imageLoader.refreshImage();
      }

      if (this.tabContent.querySelectorAll('[data-accordion]').length > 0) {
        accordion.destroyBindings();
        accordion.initBindings();
      }

      if (this.tabContent.querySelectorAll('[data-video-id]').length > 0) {
        video.initDOMElements();
        video.destroyBindings();
        video.initBindings();
      }
    }
  }

  callTealiumView(url) {
    const pieces = url.split('/');
    const currentTab = pieces[pieces.length - 1];
    window.utag.view({
      page_name: `pensions : information_centre : ${currentTab}`,
      page_url: `${url}`,
      page_type: 'article',
    });
  }

  updateTabButton(url) {
    const selectedTab = this.tabSection.querySelector(`[href="${url.split('?')[0]}"]`);
    selectedTab.classList.add('active');
    selectedTab.setAttribute('aria-selected', 'true');
  }
}

export const tab = new Tab();



// WEBPACK FOOTER //
// ./src/js/modules/tab.js